import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, Cog6ToothIcon, CheckCircleIcon, AcademicCapIcon, ExclamationTriangleIcon, EnvelopeIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import mixpanelService from '../services/mixpanelService';
import apiClient from '../utils/apiClient';

const Settings = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState(null);
  const [midiPorts, setMidiPorts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [studioInfo, setStudioInfo] = useState(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leavingStudio, setLeavingStudio] = useState(false);

  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactRequestId, setContactRequestId] = useState('');

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadStudioInfo();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load current config
      const currentConfig = api.getConfig();
      setConfig(currentConfig);

      // Fetch available MIDI ports
      const { ports } = await api.fetchMidiPorts();
      setMidiPorts(ports || []);

      // Fetch available models - 135116 is the best performing model
      const modelList = [
        { filename: 'hand_classifier_20250907_135116.pth', name: 'Best Model (135116) - Recommended' },
        { filename: 'hand_classifier_20250907_120210.pth', name: 'Model 120210' },
        { filename: 'hand_classifier_20250907_105356.pth', name: 'Model 105356' },
        { filename: 'hand_classifier_20250907_135329.pth', name: 'Model 135329' }
      ];
      setModels(modelList);

      // Set default model if not configured
      if (!currentConfig.model) {
        currentConfig.model = 'hand_classifier_20250907_135116.pth';
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudioInfo = async () => {
    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStudioInfo(data);
      }
    } catch (error) {
      console.error('Failed to load studio info:', error);
    }
  };

  const handleLeaveStudio = async () => {
    setLeavingStudio(true);
    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/studio/leave`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setStudioInfo({ ...studioInfo, studio: null });
        setShowLeaveConfirm(false);
        mixpanelService.track('Studio Left');
      } else {
        console.error('Failed to leave studio');
      }
    } catch (error) {
      console.error('Error leaving studio:', error);
      mixpanelService.trackFailure('leave_studio', error);
    } finally {
      setLeavingStudio(false);
    }
  };

  const handleSave = async () => {
    api.saveConfig(config);

    // Load the selected model if it changed
    try {
      await api.loadModel(config.model);
    } catch (error) {
      console.error('Failed to load model:', error);
    }

    setSaved(true);
    mixpanelService.track('Settings Saved', {
      midi_configured: Boolean(config.midiPort),
      auto_start_recording: Boolean(config.autoStartRecording),
      recording_buffer_seconds: config.recordingBufferSeconds
    });
    setTimeout(() => {
      setSaved(false);
      onClose();
    }, 1500);
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleContactSubmit = async () => {
    setContactSubmitting(true);
    setContactError('');
    setContactRequestId('');

    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/contact`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        const data = await response.json();
        setContactRequestId(data.request_id || '');
        setContactSuccess(true);
        mixpanelService.track('Support Request Sent');
      } else {
        const error = await response.json();
        setContactRequestId(error.request_id || '');
        setContactError(error.detail || 'Failed to send message');
        mixpanelService.track('Support Request Failed', {
          status_code: response.status,
          failure_mode: error.detail || 'Failed to send message'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setContactError('Failed to send message. Please try again.');
      mixpanelService.trackFailure('support_request', error);
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleContactChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm');
      return;
    }

    setDeletingAccount(true);
    setDeleteError('');

    try {
      const response = await apiClient.fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/delete-account`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        mixpanelService.track('Account Deleted');
        // Clear all local storage and session storage
        localStorage.clear();
        sessionStorage.clear();

        // Redirect to login page
        window.location.href = '/';
      } else {
        const error = await response.json();
        setDeleteError(error.detail || 'Failed to delete account');
        mixpanelService.track('Account Deletion Failed', {
          status_code: response.status,
          failure_mode: error.detail || 'Failed to delete account'
        });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError('Failed to delete account. Please try again.');
      mixpanelService.trackFailure('account_deletion', error);
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!config) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex items-center justify-between mb-4"
                >
                  <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                    <Cog6ToothIcon className="h-5 w-5" />
                    <span>Settings</span>
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>

                {loading ? (
                  <div className="py-8 text-center text-gray-500">
                    Loading settings...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* MIDI Port Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        MIDI Input Device
                      </label>
                      <select
                        value={config.midiPort || ''}
                        onChange={(e) => handleChange('midiPort', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Select a MIDI device...</option>
                        {midiPorts.map(port => (
                          <option key={port} value={port}>{port}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        The MIDI device to use for recording
                      </p>
                    </div>

                    {/* Model Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hand Classification Model
                      </label>
                      <select
                        value={config.model || ''}
                        onChange={(e) => handleChange('model', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {models.map(model => (
                          <option key={model.filename} value={model.filename}>
                            {model.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        The AI model used for hand separation
                      </p>
                    </div>

                    {/* Recording Buffer */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Recording Buffer (seconds)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="30"
                        value={config.recordingBufferSeconds || 10}
                        onChange={(e) => handleChange('recordingBufferSeconds', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        How many seconds of MIDI data to show in the piano roll
                      </p>
                    </div>

                    {/* Auto-start Recording */}
                    <div>
                      <label className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={config.autoStartRecording || false}
                          onChange={(e) => handleChange('autoStartRecording', e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Auto-start recording on launch
                        </span>
                      </label>
                      <p className="mt-1 ml-7 text-xs text-gray-500">
                        Automatically begin recording when the app starts
                      </p>
                    </div>
                  </div>
                )}

                {/* Studio Information */}
                {studioInfo && studioInfo.studio && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <AcademicCapIcon className="h-5 w-5" />
                        <span>Studio</span>
                      </label>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-700 font-medium">{studioInfo.studio.name}</p>
                      {studioInfo.role === 'teacher' ? (
                        <p className="text-xs text-gray-500 mt-1">You are the teacher of this studio</p>
                      ) : (
                        <div className="mt-2">
                          <button
                            onClick={() => setShowLeaveConfirm(true)}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                          >
                            Leave Studio
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Button */}
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 flex items-center justify-center space-x-2"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                    <span>Contact Support</span>
                  </button>
                </div>

                {/* Delete Account Button */}
                <div className="mt-4">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center space-x-2"
                  >
                    <TrashIcon className="h-5 w-5" />
                    <span>Delete Account</span>
                  </button>
                </div>

                <div className="mt-4 text-center text-xs text-gray-500">
                  <a className="hover:text-indigo-600" href="https://practicemeter.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
                  <span className="mx-2">·</span>
                  <a className="hover:text-indigo-600" href="https://practicemeter.com/terms" target="_blank" rel="noreferrer">Terms</a>
                  <span className="mx-2">·</span>
                  <a className="hover:text-indigo-600" href="https://practicemeter.com/cancellation" target="_blank" rel="noreferrer">Cancellation</a>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn-primary flex items-center space-x-2"
                    disabled={loading || saved}
                  >
                    {saved ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5" />
                        <span>Saved!</span>
                      </>
                    ) : (
                      <span>Save Settings</span>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Leave Studio Confirmation Modal */}
      <Transition appear show={showLeaveConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowLeaveConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Leave Studio?
                    </Dialog.Title>
                  </div>

                  <p className="text-sm text-gray-500 mb-6">
                    Leaving immediately ends your teacher's access to your practice history.
                    Your account and saved practice data stay with you, and you will need a new
                    invitation to rejoin.
                  </p>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowLeaveConfirm(false)}
                      className="btn-secondary"
                      disabled={leavingStudio}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLeaveStudio}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      disabled={leavingStudio}
                    >
                      {leavingStudio ? 'Leaving...' : 'Leave Studio'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Contact Form Modal */}
      <Transition appear show={showContactForm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowContactForm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="div"
                    className="flex items-center justify-between mb-4"
                  >
                    <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
                      <EnvelopeIcon className="h-5 w-5" />
                      <span>Contact Support</span>
                    </h3>
                    <button
                      onClick={() => setShowContactForm(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  {contactSuccess ? (
                    <div className="py-8 text-center">
                      <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-900">Request received</p>
                      <p className="text-sm text-gray-500 mt-2">Your request was saved and accepted by our email provider.</p>
                      {contactRequestId && (
                        <p className="mt-3 text-sm font-mono text-gray-700">Reference: {contactRequestId}</p>
                      )}
                      <button
                        onClick={() => {
                          setShowContactForm(false);
                          setContactSuccess(false);
                          setContactRequestId('');
                          setContactForm({ name: '', email: '', subject: '', message: '' });
                        }}
                        className="btn-primary mt-5"
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={contactForm.name}
                          onChange={(e) => handleContactChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Your name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => handleContactChange('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          value={contactForm.subject}
                          onChange={(e) => handleContactChange('subject', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="What is this about?"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Message
                        </label>
                        <textarea
                          value={contactForm.message}
                          onChange={(e) => handleContactChange('message', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows="4"
                          placeholder="Tell us more about your issue or feedback..."
                        />
                      </div>

                      {contactError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600">{contactError}</p>
                          {contactRequestId && (
                            <p className="mt-2 text-xs font-mono text-red-700">Saved as {contactRequestId}</p>
                          )}
                          <p className="mt-2 text-xs text-red-700">
                            You can also email support@practicemeter.com.
                          </p>
                        </div>
                      )}

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          onClick={() => setShowContactForm(false)}
                          className="btn-secondary"
                          disabled={contactSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleContactSubmit}
                          className="btn-primary flex items-center space-x-2"
                          disabled={contactSubmitting || !contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message}
                        >
                          {contactSubmitting ? (
                            <span>Sending...</span>
                          ) : (
                            <span>Send Message</span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Account Confirmation Modal */}
      <Transition appear show={showDeleteConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <TrashIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      Delete Account
                    </Dialog.Title>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                      <p className="text-sm text-red-800 font-medium mb-2">
                        ⚠️ This action cannot be undone!
                      </p>
                      <p className="text-sm text-red-700">
                        Deleting your account will permanently remove:
                      </p>
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                        <li>Your profile and settings</li>
                        <li>All practice sessions and progress data</li>
                        <li>Studio memberships</li>
                        <li>Your active subscription and saved payment methods in Stripe</li>
                      </ul>
                      <p className="mt-2 text-xs text-red-700">
                        Limited billing, tax, fraud-prevention, support, and backup records may be retained as described in the Data Retention Policy.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To confirm, type <span className="font-mono font-bold">DELETE</span> below:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Type DELETE to confirm"
                        disabled={deletingAccount}
                      />
                    </div>

                    {deleteError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{deleteError}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                        setDeleteError('');
                      }}
                      className="btn-secondary"
                      disabled={deletingAccount}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAccount}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                    >
                      {deletingAccount ? 'Deleting...' : 'Delete Account'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Transition>
  );
};

export default Settings;
